import React, {
    useCallback, useEffect, useState 
} from 'react'
import { useHistory } from 'react-router-dom'
import { Formik, Form } from 'formik'
import { FaArrowRight } from 'react-icons/fa'
import { toast } from 'react-toastify'
import { format, addDays, addYears } from 'date-fns'

import Yup from '~/services/yup'
import { api } from '~/services/api'

import { descriptografar } from '~/util/crypto'
import masks from '~/util/masks'
import formUtils from '~/util/form'

import {
    Textbox, Button, Select, File 
} from '~/components/Form'
import ActionFooter from '~/components/ActionFooter'

import { Container } from './styles'

const idadeMinima = 20

const validation = Yup.object({
    crm: Yup.number()
        .typeError('Digite apenas números.')
        .min(1, 'O CRM não pode ser zero.')
        .max(999999, 'O CRM tem apenas 6 dígitos.')
        .required('Campo obrigatório.'),
    nome: Yup.string().min(3, 'O nome deve ter pelo menos 3 caracteres.').required('O nome é obrigatório'),
    especialidade: Yup.string().nullable().required('A especialidade é obrigatória'),
    data_nascimento: Yup.string().date({ max: { value: addYears(new Date(), -idadeMinima), message: `Você deve ter pelo menos ${idadeMinima} anos para se associar.` } }).required('Especifique a data de nascimento.'),
    sexo: Yup.string().ensure().required('Especifique o sexo.'),
    email: Yup.string().email('E-mail inválido.').required('Especifique um e-mail.'),
    situacao: Yup.string().required('A situação do CRM deve ser preenchida'),
    cpf: Yup.string().cpf('CPF inválido').required('Especifique o CPF'),
    'cpf-arquivo': Yup.number().nullable(),
    rg: Yup.string().required('Especifique o RG'),
    'rg-arquivo': Yup.number().nullable()
})

export default function ({ callback, loadStoredData }) {
    const history = useHistory()

    const [especialidades, setEspecialidades] = useState([])
    const [loadingData, setLoadingData] = useState(false)
    const [situacaoIrregular, setSituacaoIrregular] = useState(false)
    const [initialValues, setInitialValues] = useState({ 
        crm: '',
        nome: '',
        situacao: '',
        especialidade: '',
        data_nascimento: '',
        sexo: '',
        email: '',
        cpf: '',
        'cpf-arquivo': null,
        rg: '',
        'rg-arquivo': null
    })

    async function setStoredData(data) {
        const { data_nascimento, sexo } = data

        setInitialValues({ 
            ...data,
            data_nascimento: format(addDays(new Date(data_nascimento), 1), 'dd/MM/yyyy'),
            sexo: { label: sexo === 'M' ? 'Masculino' : 'Feminino', value: sexo }
        })
    }

    async function loadEspecialidades() {
        const response = await api.get('/especialidade')

        const serializado = response.map(especialidade => ({
            label: especialidade.descricao,
            value: especialidade.id
        }))

        setEspecialidades(serializado)
    }

    useEffect(() => {
        loadEspecialidades()        
    }, [])

    useEffect(() => {
        const storageData = JSON.parse(localStorage.getItem('sindmepa-associacao-temp-data'))

        if(storageData?.pessoal) {
            setInitialValues({
                ...storageData.pessoal,
                data_nascimento: format(addDays(new Date(storageData.pessoal.data_nascimento), 1), 'dd/MM/yyyy'),
                sexo: {
                    label: storageData.pessoal.sexo === 'M' ? 'Masculino' : 'Feminino',
                    value: storageData.pessoal.sexo
                }
            })
        }
    }, [])

    const handleSubmit = useCallback(async (values, { setSubmitting }) => {
        const data = formUtils.extractFormValues(values)

        const userData = descriptografar(sessionStorage.getItem(process.env.REACT_APP_SESSION_STORAGE_USER))

        const storageData = JSON.parse(localStorage.getItem('sindmepa-associacao-temp-data'))

        localStorage.setItem('sindmepa-associacao-temp-data', JSON.stringify({ 
            ...storageData,
            user_id: storageData.user_id || userData?.id || null,
            pessoal: data,
            academico: false
        }))

        setSubmitting(false)

        toast.success('Dados salvos! Por favor, continue o preenchimento.')
            
        callback()
    }, [callback])

    const handleCRMBlur = useCallback(async (value, { setFieldValue, resetForm }) => {
        if(!value) {
            return
        }

        const crm = Number(value)

        setFieldValue('crm', crm.toString())
		
        setLoadingData(true)

        let dadosCFM

        try {
            dadosCFM = await api.get(`consulta_cfm/PA/${crm}`)

            if(dadosCFM.codigoErro) {
                throw new Error()
            }
        } catch(e) {
            toast.warn('O sistema de verificação da CFM está indisponível. Por favor, tente novamente dentro de alguns minutos.')
            setLoadingData(false)
            return
        }

        if(!dadosCFM) {
            toast.warn('CRM não encontrado. Verifique o número digitado.')
            setLoadingData(false)
            return
        }

        // Verificação de associação
        const associados = await api.get(`associado?crm=${crm}`)
        const associado = associados?.[0]

        switch(associado?.status_associacao.id) {
            case 2:
                setLoadingData(false)
            
                toast.info('Você já solicitou sua associação. Basta aguardar o contato do SINDMEPA.', {
                    autoClose: 8000
                })

                resetForm()
                
                return
            case 3:
                setLoadingData(false)
            
                toast.info('Você já está associado ao SINDMEPA. Faça login no Espaço Médico e acesse suas informações.', {
                    autoClose: 6000
                })

                setTimeout(() => { history.push('/') }, 2000)
                
                return
            default: break
        }

        const {
            nome, especialidade: listaEspecialidades, situacao: situacaoCodigo 
        } = dadosCFM

        if(!nome) {
            toast.error('CRM não encontrado!')
            setLoadingData(false)
            return
        }

        const { descricao: situacao } = await api.get(`situacao_associacao/${situacaoCodigo}`)

        if(situacaoCodigo !== 'A') {
            setSituacaoIrregular(true)
            setLoadingData(false)
            toast.info(`Sua situação consta como ${situacao}. Contate o Conselho Federal de Medicina e regularize para continuar.`, { autoClose: 6000 })
            return
        }

        setSituacaoIrregular(false)

        loadStoredData(crm, setStoredData)

        const response = {
            nome,
            situacao,
            especialidade: listaEspecialidades?.length ? listaEspecialidades.filter(espec => !!especialidades
                .find(esp => esp.label.toLowerCase() === espec.toLowerCase().split(' - ')[0]))
                .map(espec => {
                    const especialidade = especialidades.find(esp => esp.label.toLowerCase() === espec.toLowerCase().split(' - ')[0])

                    if(!especialidade) {
                        toast.warn(`Especialidade ${espec} não consta na lista.`)
                    }

                    return {
                        label: especialidade.label.split(' - ')[0],
                        value: especialidade.value
                    }
                }) : []
        }

        setFieldValue('nome', response.nome)
        setFieldValue('situacao', response.situacao)
        setFieldValue('especialidade', response.especialidade)

        toast.info('Seu nome será preenchido automaticamente. Verifique se está correto antes de continuar.')

        setLoadingData(false)
    }, [especialidades, history, loadStoredData])
    
    return (
        <Container>
            <Formik
                initialValues={initialValues}
                validationSchema={validation}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({
                    isSubmitting, setFieldValue, resetForm, errors, values 
                }) => (
                    <Form>
                        <Textbox 
                            label="Informe o CRM" 
                            name="crm" 
                            type="number"
                            onBlur={e => handleCRMBlur(e.target.value, { setFieldValue, resetForm })}
                        />

                        <Textbox
                            label="Nome completo"
                            name="nome"
                            readOnly
                            loading={loadingData} 
                            tooltip={values.nome && 'Este é o nome vinculado ao CRM digitado,<br>conforme o cadastro do Conselho Federal de Medicina.<br>Caso haja alguma inconsistênca, entre em contato<br>com o SINDMEPA para solucionar.'}
                        />
                        
                        <Select 
                            name="especialidade"
                            label="Especialidade"
                            onChange={selected => setFieldValue('especialidade', selected)}
                            options={especialidades}
                            isMulti
                            containerStyle={{ gridArea: 'espec' }}
                        />

                        <Textbox
                            label="Situação do CRM"
                            name="situacao"
                            readOnly 
                            loading={loadingData}
                        />

                        <Textbox 
                            name="data_nascimento" 
                            label="Data de nascimento"
                            inputMode="numeric"
                            mask={masks.date} 
                        />

                        <Select 
                            name="sexo"
                            label="Sexo"
                            onChange={selected => setFieldValue('sexo', selected)}
                            options={[
                                { label: 'Masculino', value: 'M' },
                                { label: 'Feminino', value: 'F' }
                            ]}
                            isSearchable={false}
                        />

                        <Textbox 
                            name="email" 
                            label="E-mail"
                            inputMode="email"
                            style={{ textTransform: 'lowercase' }}
                        />

                        <Textbox 
                            name="cpf" 
                            label="CPF"
                            mask={masks.cpf} 
                            inputMode="numeric"
                        />

                        <File 
                            name="cpf-arquivo"
                            onSuccess={fileId => setFieldValue('cpf-arquivo', fileId)}
                            label="CPF digitalizado"
                            format="square"
                            previewSize={['100%', '150px']}
                            error={errors['cpf-arquivo']}
                            getPreloadImage={async () => {
                                const arquivo = await api.get(`arquivo/${values['cpf-arquivo']}`)
                                return arquivo?.link
                            }}
                            style={{ gridArea: 'cpf_arquivo' }}
                        />

                        <Textbox 
                            name="rg" 
                            label="RG"
                            type="number"
                        />

                        <File 
                            name="rg-arquivo"
                            onSuccess={fileId => setFieldValue('rg-arquivo', fileId)}
                            label="RG digitalizado"
                            format="square"
                            previewSize={['100%', '150px']}
                            error={errors['rg-arquivo']}
                            getPreloadImage={async () => {
                                const arquivo = await api.get(`arquivo/${values['rg-arquivo']}`)
                                return arquivo?.link
                            }}
                            style={{ gridArea: 'rg_arquivo' }}
                        />

                        <ActionFooter>
                            <Button type="submit" disabled={situacaoIrregular || loadingData} loading={isSubmitting} className="white">
                                Continuar
                                <FaArrowRight />
                            </Button>
                        </ActionFooter>
                    </Form>
                )}
            </Formik>
        </Container>
    )
}
